import React from "react"
import type { Scope } from "@sentry/browser"

declare global {
  interface Window {
    Sentry: {
      withScope: typeof import("@sentry/browser").withScope
      captureException: typeof import("@sentry/browser").captureException
      showReportDialog: typeof import("@sentry/browser").showReportDialog
    }
  }
}

export const wrapRootElement = ({
  element,
}: {
  element: React.ReactElement
}): React.ReactElement => {
  if (process.env.NODE_ENV != "production") return element

  return <ErrorBoundary>{element}</ErrorBoundary>
}

const errorHandler = (error: Error | null, componentStack: string) => {
  window.Sentry.withScope((scope: Scope) => {
    scope.setExtra("componentStack", componentStack)
    window.Sentry.captureException(error)
  })
}

export const ErrorBoundaryFallbackComponent = (): React.ReactElement => (
  <div>
    <h1>An error has occurred.</h1>
    <a onClick={() => window.Sentry.showReportDialog()}>Report feedback</a>
  </div>
)

interface ErrorState {
  error: Error | null
  info: React.ErrorInfo | null
}

interface ErrorBoundaryProps {
  children: React.ReactNode
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      error: null,
      info: null,
    }
  }

  public componentDidCatch(error: Error | null, info: React.ErrorInfo) {
    try {
      errorHandler.call(this, error, info?.componentStack || "")
      //eslint-disable-next-line no-empty
    } catch {}

    this.setState({ error, info })
  }

  public render() {
    const { children } = this.props
    const { error } = this.state

    if (error !== null) return <ErrorBoundaryFallbackComponent />

    return children
  }
}
